import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, Edit2, DollarSign, UserPlus, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const fmt = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

const InvestorsPage = () => {
  const [investors, setInvestors] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showInvestModal, setShowInvestModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', investor_type: 'individual', contact_email: '', contact_phone: '', id_number: '', registration_number: '', address: '', notes: '' });
  const [investForm, setInvestForm] = useState({ investor_id: '', fund_id: '', amount: '', notes: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [invRes, fundsRes] = await Promise.all([api.get('/investors'), api.get('/funds')]);
      setInvestors(invRes.data); setFunds(fundsRes.data);
    } catch { setInvestors([]); setFunds([]); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.name) return toast.error('Name is required');
    try {
      if (editId) { await api.put(`/investors/${editId}`, form); toast.success('Updated'); }
      else { await api.post('/investors', form); toast.success('Investor created'); }
      resetForm(); load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleInvest = async () => {
    if (!investForm.investor_id || !investForm.fund_id || !investForm.amount) return toast.error('All fields required');
    try {
      const res = await api.post('/fund-investments', { ...investForm, amount: parseFloat(investForm.amount) });
      toast.success(res.data.message || 'Investment recorded');
      setShowInvestModal(false);
      setInvestForm({ investor_id: '', fund_id: '', amount: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = (inv) => {
    setEditId(inv.id);
    setForm({ name: inv.name || '', investor_type: inv.investor_type || 'individual', contact_email: inv.contact_email || '', contact_phone: inv.contact_phone || '', id_number: inv.id_number || '', registration_number: inv.registration_number || '', address: inv.address || '', notes: inv.notes || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this investor?')) return;
    try { await api.delete(`/investors/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const resetForm = () => {
    setShowModal(false); setEditId(null);
    setForm({ name: '', investor_type: 'individual', contact_email: '', contact_phone: '', id_number: '', registration_number: '', address: '', notes: '' });
  };

  return (
    <AdminSubPage title="Investors" breadcrumb={['Admin', 'Organization', 'Investors']} backPath="/dashboard/admin/organization">
      <div className="flex items-center gap-3 mb-4">
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#1f1f1f] text-white" data-testid="add-investor-btn"><UserPlus className="w-4 h-4 mr-2" />Add Investor</Button>
        <Button variant="outline" onClick={() => setShowInvestModal(true)} data-testid="record-investment-btn"><DollarSign className="w-4 h-4 mr-2" />Record Investment</Button>
      </div>
      <p className="text-xs text-gray-500 mb-4">Manage investors and link their capital contributions to specific funds. Each investment automatically increases the fund's total capital.</p>

      <div className="bg-white border border-gray-200" data-testid="investors-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Investor</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Contact</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Funds</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Total Invested</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            investors.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">No investors yet</td></tr> :
            investors.map(inv => (
              <tr key={inv.id} className="border-b hover:bg-gray-50" data-testid={`investor-row-${inv.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{inv.name}</div>{inv.id_number && <div className="text-xs text-gray-400">ID: {inv.id_number}</div>}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 bg-gray-50 text-gray-600 capitalize">{inv.investor_type}</span></td>
                <td className="px-4 py-3 text-xs text-gray-500">{inv.contact_email || inv.contact_phone || '-'}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{inv.funds?.join(', ') || 'None'}</td>
                <td className="px-4 py-3 text-sm text-right font-mono font-medium text-[#C5A059]">{fmt(inv.total_invested)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(inv)} className="text-gray-500"><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(inv.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Investor */}
      <Dialog open={showModal} onOpenChange={v => { if (!v) resetForm(); }}>
        <DialogContent aria-describedby="inv-desc"><DialogHeader><DialogTitle>{editId ? 'Edit Investor' : 'Add Investor'}</DialogTitle></DialogHeader>
          <p id="inv-desc" className="sr-only">Investor form</p>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="investor-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={form.investor_type} onValueChange={v => setForm({...form, investor_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="institutional">Institutional</SelectItem>
                    <SelectItem value="dfi">DFI / Development Finance</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="donor">Donor / NGO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Email</Label><Input value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} /></div>
              <div><Label>{form.investor_type === 'individual' ? 'ID Number' : 'Registration No.'}</Label><Input value={form.investor_type === 'individual' ? form.id_number : form.registration_number} onChange={e => setForm({...form, [form.investor_type === 'individual' ? 'id_number' : 'registration_number']: e.target.value})} /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={resetForm}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="investor-submit">{editId ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Investment */}
      <Dialog open={showInvestModal} onOpenChange={v => { if (!v) setShowInvestModal(false); }}>
        <DialogContent aria-describedby="invest-desc"><DialogHeader><DialogTitle>Record Investment</DialogTitle></DialogHeader>
          <p id="invest-desc" className="sr-only">Link an investor contribution to a fund</p>
          <div className="space-y-4">
            <div><Label>Investor *</Label>
              <Select value={investForm.investor_id} onValueChange={v => setInvestForm({...investForm, investor_id: v})}>
                <SelectTrigger data-testid="invest-investor"><SelectValue placeholder="Select investor" /></SelectTrigger>
                <SelectContent>{investors.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Fund *</Label>
              <Select value={investForm.fund_id} onValueChange={v => setInvestForm({...investForm, fund_id: v})}>
                <SelectTrigger data-testid="invest-fund"><SelectValue placeholder="Select fund" /></SelectTrigger>
                <SelectContent>{funds.map(f => <SelectItem key={f.id} value={f.id}>{f.name} ({fmt(f.available)} available)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Amount (R) *</Label><Input type="number" value={investForm.amount} onChange={e => setInvestForm({...investForm, amount: e.target.value})} data-testid="invest-amount" /></div>
            <div><Label>Notes</Label><Input value={investForm.notes} onChange={e => setInvestForm({...investForm, notes: e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowInvestModal(false)}>Cancel</Button><Button onClick={handleInvest} className="bg-[#1f1f1f] text-white" data-testid="invest-submit">Record Investment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default InvestorsPage;
